// TanStack Query wiring: one QueryClient whose caches route every failed query or mutation
// through the problem → toast mapper. Refetch on focus and every 60 s (spec 13 §Screens) stays
// as the floor; against the real API, change pings (live/, RT-01; ADR-0017) invalidate the
// keys of what another session changed, so screens update within the 2 s projection budget.
import { ApiError } from "@companyos/sdk";
import { MutationCache, QueryCache, QueryClient, queryOptions } from "@tanstack/react-query";
import { operatorOnlyIn } from "../features/company";
import type { Api } from "./client";
import { notifyProblem, type ToastSink } from "./problem-toast";
import type { MeResponse } from "./types";

/**
 * OPERATOR-ONLY-UX: a read under `["company", co, …]` refused with a plain 403 while the caller
 * is operator-only in `co` (an agency grant, no person membership). The screen shows the one
 * inline notice for it (features/operator-only.tsx), so no toast is raised.
 */
export function quietOperatorForbidden(
  me: MeResponse | undefined,
  queryKey: readonly unknown[],
  error: unknown,
): boolean {
  if (!me || queryKey[0] !== "company" || typeof queryKey[1] !== "string") return false;
  if (!(error instanceof ApiError) || error.status !== 403 || error.code !== "forbidden")
    return false;
  return operatorOnlyIn(me, queryKey[1]);
}

/**
 * DE-KPIS-MEMBER: a read that lists `meta.quietStatuses` and is refused with one of them is an
 * expected permission case the screen words itself (DE KPIs: "Restricted"), so no toast is raised.
 */
export function quietStatus(meta: Record<string, unknown> | undefined, error: unknown): boolean {
  const quiet = meta?.quietStatuses;
  return error instanceof ApiError && Array.isArray(quiet) && quiet.includes(error.status);
}

export function createQueryClient(toasts: ToastSink): QueryClient {
  const onError = (error: unknown) => notifyProblem(error, toasts);
  let client: QueryClient | undefined;
  const onQueryError = (
    error: unknown,
    query: { queryKey: readonly unknown[]; meta?: Record<string, unknown> | undefined },
  ) => {
    const me = client?.getQueryData<MeResponse>(["me"]);
    if (quietOperatorForbidden(me, query.queryKey, error)) return;
    if (quietStatus(query.meta, error)) return;
    onError(error);
  };
  client = new QueryClient({
    queryCache: new QueryCache({ onError: onQueryError }),
    // A mutation whose `meta.quietCodes` lists the problem's code shows it itself (SHARED-DES:
    // an assign refused with `not_in_reach` raises the "Ask for access" toast instead).
    mutationCache: new MutationCache({
      onError: (error, _v, _c, mutation) => {
        const quiet = mutation.meta?.quietCodes;
        if (error instanceof ApiError && Array.isArray(quiet) && quiet.includes(error.code)) return;
        onError(error);
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        refetchInterval: 60_000,
        staleTime: 30_000,
        retry: (count, error) =>
          count < 2 && !(error instanceof Error && "status" in error && Number(error.status) < 500),
      },
    },
  });
  return client;
}

export function meQuery(api: Api) {
  return queryOptions({
    queryKey: ["me"],
    queryFn: async () => (await api.client.me.get()) as MeResponse,
  });
}
